import { z } from 'zod';
import { BrandMealPeriodSchema } from './mealPeriods';
import { objectIdSchema } from './objectId';
import { CommonTextTranslationSchema } from './translation';
import { VenueLocationLat, VenueLocationLong } from './const';
import { BrandSegmentSchema } from './brandSegment';
import { BrandLevelSchema } from './brandLevel';
import { BrandAttributeSchema } from './brandAttribute';
import { BrandStaffDepartmentSchema } from './brandStaffDepartment';
import { BrandEventHighlightSchema } from './brandEventHighlight';

export const HourFormat = ['12', '24'] as const;
const brandCommonTextVenueLocationLongLabel = VenueLocationLong.map(loc => `venue_location_${loc}`);
export const BrandCommonTextVenueLocationLongLabel = [
  ...brandCommonTextVenueLocationLongLabel,
] as const;

const brandCommonTextVenueLocationLatLabel = VenueLocationLat.map(loc => `venue_location_${loc}`);
export const BrandCommonTextVenueLocationLatLabel = [
  ...brandCommonTextVenueLocationLatLabel,
] as const;

export const BrandCommonTextLabel = [
  'venue_closed',
  'ends_late',
  '24_7',
  'override_start_time',
  'deck',
  'tour_leader',
  'tour_leader_cabin',
  'guest_experience',
  'group_contact_name',
  'guests',
  'embark_date',
  'debark_date',
  'group_booking',
  ...BrandCommonTextVenueLocationLongLabel,
  ...BrandCommonTextVenueLocationLatLabel,
] as const;

export const BrandCommonTextCategory = ['venue', 'display_options', 'groups_label'] as const;

const dateComponentOptions = z.enum(['numeric', '2-digit', 'short', 'long', 'narrow']);

export const BrandVenueTypeSchema = z.object({
  _id: objectIdSchema,
  name: z.string(),
  hideFromSignage: z.boolean().optional(),
});

export const BrandGroupResourceTypeSchema = z.object({
  _id: objectIdSchema,
  name: z.string(),
});

export const BrandDateFormatSchema = z
  .object({
    weekday: dateComponentOptions,
    month: dateComponentOptions,
    year: dateComponentOptions,
    day: dateComponentOptions,
    useOrdinals: z.boolean().optional(),
  })
  .partial();

export const BrandFontSizeSchema = z.object({
  min: z.number(),
  max: z.number(),
});

export const BrandNotesSchema = z.object({
  label: z.string(),
  value: z.string(),
});

export const BrandSettingsSchema = z.object({
  notes: BrandNotesSchema.array().optional(),
});

export const BrandGroupTypeSchema = z.object({
  _id: objectIdSchema,
  name: z.string(),
});

export const BrandDayThemeSchema = z.object({
  _id: objectIdSchema,
  name: z.string(),
});

export const BrandImageTypeSchema = z.object({
  _id: objectIdSchema,
  name: z.string(),
  slug: z.string().optional(),
});

export const BrandCommonTextSchema = z.object({
  _id: objectIdSchema,
  name: z.string(),
  label: z.enum(BrandCommonTextLabel),
  category: z.enum(BrandCommonTextCategory),
});

export const BrandCommonTextValuesSchema = z.object({
  venue_closed: z.string(),
  ends_late: z.string(),
  deck: z.string(),
  venue_location_fwd: z.string(),
  venue_location_mid: z.string(),
  venue_location_aft: z.string(),
  venue_location_prt: z.string(),
  venue_location_stb: z.string(),
  twenty_four_seven: z.string(),
  override_start_time: z.string(),
});

export const CommonTextsLanguageOptionsSchema = z.object({
  selected: z.boolean().optional(),
});

export const CommonTextsSchema = z.object({
  default: BrandCommonTextSchema.array(),
  translations: CommonTextTranslationSchema.array(),
});

export const BrandSchema = z.object({
  _id: objectIdSchema,
  name: z.string(),
  logo: z.string(),
  active: z.boolean().optional().default(true),
  segments: z.array(BrandSegmentSchema),
  levels: z.array(BrandLevelSchema),
  attributes: z.array(BrandAttributeSchema),
  venueTypes: z.array(BrandVenueTypeSchema),
  hourFormat: z.enum(HourFormat),
  staffDepartments: z.array(BrandStaffDepartmentSchema).optional(),
  settings: BrandSettingsSchema.optional(),
  groupTypes: z.array(BrandGroupTypeSchema),
  mealPeriods: z.array(BrandMealPeriodSchema),
  dayThemes: z.array(BrandDayThemeSchema),
  imageTypes: z.array(BrandImageTypeSchema),
  eventHighlights: z.array(BrandEventHighlightSchema),
  commonTexts: z.array(BrandCommonTextSchema),
  fontSize: BrandFontSizeSchema,
  dateFormat: BrandDateFormatSchema.optional(),
  groupResourceTypes: z.array(BrandGroupResourceTypeSchema),
});

export type Brand = z.infer<typeof BrandSchema>;
export type BrandGroupResourceType = z.infer<typeof BrandGroupResourceTypeSchema>;
